/**
 * Drives animation from the server (or local session) event stream. [P6-anim]
 *
 * Every effect here is scheduled through `TweenGroup`, so `skipAll()` — called when a reconnecting or
 * impatient client wants to jump straight to the current snapshot — always leaves the board in a
 * correct end state instead of a half-finished one. Nothing here blocks a subsequent dispatch; the
 * board is fully interactive while tweens are still running.
 */

import { Vector3 } from 'three';
import type { GameEvent, GameState, Pos, Meeple } from '@carc/engine';
import { posKey } from '@carc/engine';
import type { BoardView } from './board-view.js';
import type { Scene } from './scene.js';
import type { SoundBoard } from './audio.js';
import { TILE_HEIGHT, worldPosition } from './tile-mesh.js';
import { easeOutBack, easeInCubic, tween, TweenGroup } from './tween.js';

export type SeatColourLookup = (seat: number) => string;

export class AnimationDirector {
  private readonly tweens = new TweenGroup();
  private readonly popupLayer: HTMLElement;
  private readonly untick: () => void;

  constructor(
    private readonly board: BoardView,
    private readonly scene: Scene,
    private readonly seatColour: SeatColourLookup,
    private readonly sounds: SoundBoard,
  ) {
    this.popupLayer = document.createElement('div');
    this.popupLayer.className = 'anim-layer';
    Object.assign(this.popupLayer.style, {
      position: 'fixed',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '30',
    });
    document.body.appendChild(this.popupLayer);
    this.untick = scene.onTick((dt) => this.tweens.update(dt));
  }

  /** Session-scoped: drop the score-popup layer and stop driving tweens. */
  dispose(): void {
    this.tweens.skipAll();
    this.untick();
    this.popupLayer.remove();
  }

  skipAll(): void {
    this.tweens.skipAll();
  }

  handle(events: readonly GameEvent[], state: GameState): void {
    for (const event of events) this.handleOne(event, state);
  }

  private handleOne(event: GameEvent, state: GameState): void {
    switch (event.type) {
      case 'tile_placed':
        this.animateTilePlaced(event.pos, state);
        this.sounds.play('place');
        break;
      case 'meeple_placed':
        this.animateMeeplePlaced(event.meeple);
        this.sounds.play('meeple');
        break;
      case 'meeples_returned':
        for (const meeple of event.meeples) this.animateMeepleReturn(meeple);
        break;
      case 'feature_scored':
        this.animateFeatureScored(event);
        break;
      case 'turn_changed':
        this.sounds.play('turn');
        break;
      default:
        break;
    }
  }

  private animateTilePlaced(pos: Pos, state: GameState): void {
    if (this.board.hasTile(pos)) return; // already rendered (e.g. replay on load)
    const placed = state.board.get(posKey(pos));
    if (!placed) return;
    const mesh = this.board.addTile(placed);
    const startY = 2.4;
    mesh.group.position.y = startY;
    this.tweens.add(
      tween(
        380,
        (t) => {
          mesh.group.position.y = startY * (1 - t);
        },
        { easing: easeOutBack },
      ),
    );
  }

  private animateMeeplePlaced(meeple: Meeple): void {
    const group = this.board.addMeeple(meeple, this.seatColour(meeple.seat));
    const restY = TILE_HEIGHT;
    const startY = restY + 0.7;
    group.position.y = startY;
    group.scale.setScalar(0.4);
    this.tweens.add(
      tween(
        320,
        (t) => {
          group.position.y = startY + (restY - startY) * t;
          group.scale.setScalar(0.4 + 0.6 * t);
        },
        { easing: easeOutBack },
      ),
    );
  }

  private animateMeepleReturn(meeple: { pos: Pos; featureIndex: number }): void {
    const group = this.board.getMeepleGroup(meeple);
    if (!group) return;
    const startY = group.position.y;
    this.tweens.add(
      tween(
        420,
        (t) => {
          group.position.y = startY + t * 0.6;
          const scale = Math.max(0.001, 1 - t);
          group.scale.setScalar(scale);
        },
        { easing: easeInCubic, onComplete: () => this.board.removeMeeple(meeple) },
      ),
    );
  }

  private animateFeatureScored(event: Extract<GameEvent, { type: 'feature_scored' }>): void {
    if (event.tiles.length === 0) return;
    if (event.completed) {
      this.board.flashFeature(event.tiles, 900);
    }
    if (event.points <= 0 || event.seats.length === 0) return;

    this.sounds.play('score');
    const centroid = this.centroid(event.tiles);
    event.seats.forEach((seat, i) => {
      this.spawnScorePopup(centroid, `+${event.points}`, this.seatColour(seat), i);
    });
  }

  private centroid(positions: readonly Pos[]): Vector3 {
    const v = new Vector3();
    for (const pos of positions) {
      const [x, y, z] = worldPosition(pos);
      v.add(new Vector3(x, y + TILE_HEIGHT + 0.3, z));
    }
    v.divideScalar(Math.max(1, positions.length));
    return v;
  }

  private projectToScreen(worldPos: Vector3): { x: number; y: number } {
    const v = worldPos.clone().project(this.scene.camera);
    const canvas = this.scene.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    return {
      x: rect.left + (v.x * 0.5 + 0.5) * rect.width,
      y: rect.top + (-v.y * 0.5 + 0.5) * rect.height,
    };
  }

  private spawnScorePopup(worldPos: Vector3, text: string, colourHex: string, stackIndex: number): void {
    const screen = this.projectToScreen(worldPos);
    const el = document.createElement('div');
    el.textContent = text;
    Object.assign(el.style, {
      position: 'absolute',
      left: `${screen.x}px`,
      top: `${screen.y - stackIndex * 24}px`,
      transform: 'translate(-50%, -50%)',
      color: colourHex,
      fontWeight: '800',
      fontSize: '1.35rem',
      textShadow: '0 1px 4px rgba(0,0,0,0.7), 0 0 2px rgba(0,0,0,0.9)',
      whiteSpace: 'nowrap',
    });
    this.popupLayer.appendChild(el);

    const baseTop = screen.y - stackIndex * 24;
    this.tweens.add(
      tween(
        1150,
        (t) => {
          el.style.opacity = String(1 - t);
          el.style.top = `${baseTop - t * 50}px`;
        },
        { onComplete: () => el.remove() },
      ),
    );
  }
}
